// Driving distance and time for a run, from Google Directions (the Maps
// library the app already loads), and the road line the route sheet's map
// draws from the same answer. Each route is asked for once per page load;
// Google allows 25 stops between the start and end of one request, so
// longer routes are asked for in parts.

export const MAX_WAYPOINTS = 25;

// Waits before asking Google again when it says too many requests at once.
export const RETRY_DELAYS = [1000, 2000, 4000];

/**
 * One Directions request: resolves to { result, status } as Google answers,
 * asked again (up to RETRY_DELAYS.length times) while Google says too many
 * requests at once.
 */
export const askDirections = (maps, request) =>
  new Promise((resolve) => {
    const ask = (attempt) => {
      try {
        new maps.DirectionsService().route(request, (result, status) => {
          if (status === "OVER_QUERY_LIMIT" && attempt < RETRY_DELAYS.length) {
            setTimeout(() => ask(attempt + 1), RETRY_DELAYS[attempt]);
            return;
          }
          resolve({ result, status });
        });
      } catch (error) {
        resolve({ result: null, status: "ERROR" });
      }
    };
    ask(0);
  });

// { lat, lng } of a place as Google gives it (a LatLng, or plain numbers), or
// null.
export const latLng = (place) => {
  const lat = typeof place?.lat === "function" ? place.lat() : place?.lat;
  const lng = typeof place?.lng === "function" ? place.lng() : place?.lng;
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
};

// A leg of Google's answer: { meters, seconds, from, to } (from and to: where
// Google placed its two ends, or null).
export const legOf = (leg) => ({
  meters: leg?.distance?.value ?? 0,
  seconds: leg?.duration?.value ?? 0,
  from: latLng(leg?.start_location),
  to: latLng(leg?.end_location),
});

// Straight-line meters between two { lat, lng }.
export const straightMeters = (a, b) => {
  const rad = (degrees) => (degrees * Math.PI) / 180;
  const h =
    Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
    Math.cos(rad(a.lat)) *
      Math.cos(rad(b.lat)) *
      Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.min(1, Math.sqrt(h)));
};

const cache = new Map();

// The run's loop: the start location, each stop in order, back to the start.
export const loopPoints = (start, addresses) => {
  const stops = addresses.filter(Boolean);
  if (!stops.length) return [];
  return start ? [start, ...stops, start] : stops;
};

// Consecutive parts of a route, each at most MAX_WAYPOINTS stops between its
// ends; each part starts where the previous one ended.
export const routeParts = (points, max = MAX_WAYPOINTS) => {
  if (points.length < 2) return [];
  const parts = [];
  for (let from = 0; from < points.length - 1; from += max + 1) {
    parts.push(points.slice(from, Math.min(points.length, from + max + 2)));
  }
  return parts;
};

// Points closer than this to the line between the points kept around them
// are dropped from a road line: invisible on the map, and a run across a
// province keeps a few thousand points instead of tens of thousands.
export const THIN_METERS = 5;

/**
 * `path` ([{ lat, lng }]) with fewer points, none of those dropped more than
 * `tolerance` meters off the line kept (Douglas-Peucker).
 */
export const thinLine = (path, tolerance = THIN_METERS) => {
  if (path.length < 3) return path;
  // Flat meters around the first point: close enough across a province.
  const kx = 111320 * Math.cos((path[0].lat * Math.PI) / 180);
  const xy = path.map(({ lat, lng }) => [lng * kx, lat * 110574]);
  const keep = new Uint8Array(path.length);
  keep[0] = 1;
  keep[path.length - 1] = 1;
  // Spans still to check, without recursion: a long line is deep.
  const spans = [[0, path.length - 1]];
  while (spans.length) {
    const [first, last] = spans.pop();
    const [ax, ay] = xy[first];
    const dx = xy[last][0] - ax;
    const dy = xy[last][1] - ay;
    const length = dx * dx + dy * dy;
    let far = 0;
    let at = -1;
    for (let i = first + 1; i < last; i += 1) {
      const [px, py] = xy[i];
      // Off the segment, not the endless line through it (a loop's two
      // ends are the same place).
      const t = length
        ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / length))
        : 0;
      const off = Math.hypot(px - ax - t * dx, py - ay - t * dy);
      if (off > far) {
        far = off;
        at = i;
      }
    }
    if (far > tolerance) {
      keep[at] = 1;
      spans.push([first, at], [at, last]);
    }
  }
  return path.filter((_, i) => keep[i]);
};

// The road line of a route Google answered with, from its steps (a leg
// without any goes straight from one end to the other), as [{ lat, lng }].
const roadLine = (route) =>
  thinLine(
    (route?.legs ?? []).flatMap((leg) => {
      const steps = (leg?.steps ?? []).flatMap((step) => step?.path ?? []);
      return (steps.length ? steps : [leg?.start_location, leg?.end_location])
        .map(latLng)
        .filter(Boolean);
    }),
  );

const askPart = async (maps, part) => {
  const { result, status } = await askDirections(maps, {
    origin: part[0],
    destination: part[part.length - 1],
    waypoints: part
      .slice(1, -1)
      .map((location) => ({ location, stopover: true })),
    travelMode: "DRIVING",
  });
  const route = result?.routes?.[0];
  const legs = route?.legs;
  if (status !== "OK" || !Array.isArray(legs)) throw new Error(status);
  return { legs: legs.map(legOf), path: roadLine(route) };
};

// The drive through `points` as { legs, path }, or null; asked once per
// page load for the totals and the map alike (a failed one is asked again).
const askRoute = (points, maps) => {
  const key = points.join("|");
  if (!cache.has(key)) {
    cache.set(
      key,
      Promise.all(routeParts(points).map((part) => askPart(maps, part)))
        .then((parts) => ({
          legs: parts.flatMap((part) => part.legs),
          path: parts.flatMap((part) => part.path),
        }))
        .catch(() => {
          cache.delete(key);
          return null;
        }),
    );
  }
  return cache.get(key);
};

/**
 * The legs of driving through `points` in order ([{ meters, seconds, from,
 * to }], one per pair of points), or null when it can't be worked out (Maps
 * not loaded, fewer than 2 points, an address Google can't find).
 */
export const routeLegs = (points, maps = window.google?.maps) => {
  if (!maps?.DirectionsService || points.length < 2) {
    return Promise.resolve(null);
  }
  return askRoute(points, maps).then((route) => route?.legs ?? null);
};

/**
 * The drive through `points` in order with its road line, for a map:
 * { legs, path } (`legs` as routeLegs gives them, `path` [{ lat, lng }]
 * along the roads, every part of a long route), or null when it can't be
 * worked out. The same request as routeLegs and routeTotals for the same
 * points, so the map and the Drive line ask Google once between them.
 */
export const routeLine = (points, maps = window.google?.maps) => {
  if (!maps?.DirectionsService || points.length < 2) {
    return Promise.resolve(null);
  }
  return askRoute(points, maps);
};

// { meters, seconds } for a list of legs.
export const legTotals = (legs) =>
  legs.reduce(
    (sum, leg) => ({
      meters: sum.meters + leg.meters,
      seconds: sum.seconds + leg.seconds,
    }),
    { meters: 0, seconds: 0 },
  );

// { meters, seconds } for driving through `points` in order; null when it
// can't be worked out (Maps not loaded, fewer than 2 points, an address
// Google can't find).
export const routeTotals = async (points, maps = window.google?.maps) => {
  const legs = await routeLegs(points, maps);
  return legs ? legTotals(legs) : null;
};

// With `sign`, what a change adds or takes off: "+18.2 km", "−1.1 km" (a
// detour can be shorter but slower).
export const formatKm = (meters, sign = false) => {
  const km = (sign ? Math.abs(meters) : meters) / 1000;
  const text = `${km >= 100 ? Math.round(km) : Math.round(km * 10) / 10} km`;
  if (!sign) return text;
  return `${meters < 0 ? "−" : "+"}${text}`;
};

export const formatDuration = (seconds, sign = false) => {
  const minutes = Math.max(0, Math.round(Math.abs(seconds) / 60));
  const hours = Math.floor(minutes / 60);
  const text = hours
    ? `${hours} h${minutes % 60 ? ` ${minutes % 60} min` : ""}`
    : `${minutes} min`;
  if (!sign) return text;
  return `${seconds < 0 ? "−" : "+"}${text}`;
};

// Test hook: forget cached routes.
export const clearRouteCache = () => cache.clear();
